import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MagnifyingGlass, TrendUp, Users, Storefront, Stack, CaretDown, Fire
} from '@phosphor-icons/react';

export default function CreatorDiscovery() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [trending, setTrending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchType, setSearchType] = useState('all');
  const [category, setCategory] = useState('');

  useEffect(() => {
    async function loadTrending() {
      try {
        const params = category ? `?category=${category}` : '';
        const res = await api.get(`/discovery/trending${params}`);
        setTrending(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    loadTrending();
  }, [category]);

  const doSearch = useCallback(async () => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    try {
      const res = await api.get(`/discovery/search?q=${encodeURIComponent(query)}&type=${searchType}`);
      setSearchResults(res.data.results || []);
    } catch (e) {
      console.error(e);
    }
  }, [query, searchType]);

  useEffect(() => {
    const timer = setTimeout(doSearch, 400);
    return () => clearTimeout(timer);
  }, [doSearch]);

  return (
    <div className="space-y-6" data-testid="creator-discovery-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Discover</h1>
        <p className="text-sm text-zinc-500 mt-1">Find creators, offers, and collections</p>
      </div>

      {/* Search Bar */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search creators, offers, collections..."
                className="pl-10 bg-zinc-800 border-zinc-700 h-10"
              />
            </div>
            <div className="relative w-36">
              <select
                data-testid="search-type"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="w-full h-10 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="creators">Creators</option>
                <option value="offers">Offers</option>
                <option value="collections">Collections</option>
              </select>
              <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Search Results ({searchResults.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {searchResults.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                onClick={() => {
                  if (item._type === 'creator' && item.custom_url) navigate(`/storefront/${item.custom_url}`);
                  else if (item._type === 'offer') navigate('/marketplace');
                  else if (item._type === 'collection') navigate('/discover');
                }}
                data-testid={`search-result-${i}`}
              >
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={`text-[10px] ${
                    item._type === 'creator' ? 'text-purple-400 border-purple-700/40' :
                    item._type === 'offer' ? 'text-emerald-400 border-emerald-700/40' :
                    'text-blue-400 border-blue-700/40'
                  }`}>{item._type}</Badge>
                  <div>
                    <p className="text-sm text-zinc-200">{item.display_name || item.title || item.name}</p>
                    <p className="text-xs text-zinc-500 truncate max-w-[400px]">{item.bio || item.description || ''}</p>
                  </div>
                </div>
                {item._type === 'creator' && (
                  <span className="text-xs text-zinc-500">{item.subscriber_count || 0} followers</span>
                )}
                {item._type === 'offer' && (
                  <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 text-xs">${item.payout}</Badge>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Category Filter */}
      {trending?.categories && (
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setCategory('')}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${!category ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'}`}
          >All</button>
          {trending.categories.slice(0, 10).map(cat => (
            <button key={cat} onClick={() => setCategory(cat)} data-testid={`discover-cat-${cat}`}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${category === cat ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'}`}
            >{cat}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center text-zinc-500 py-8">Loading trending content...</div>
      ) : (
        <>
          {/* Trending Creators */}
          {trending?.top_creators?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Fire size={20} className="text-orange-400" /> Top Creators</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {trending.top_creators.map((creator, i) => (
                  <Card key={creator.profile_id || i} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                    onClick={() => creator.custom_url && navigate(`/storefront/${creator.custom_url}`)}
                    data-testid={`trending-creator-${i}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-bold text-white shrink-0">
                          {creator.display_name?.[0] || 'C'}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-zinc-200 truncate">{creator.display_name}</p>
                          <p className="text-[10px] text-zinc-500 truncate">{creator.bio?.substring(0, 60)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-3 text-[10px] text-zinc-500">
                        <span><Users size={12} className="inline mr-0.5" /> {creator.subscriber_count || 0}</span>
                        <span className="flex-1" />
                        {creator.categories?.slice(0, 2).map(c => (
                          <span key={c} className="px-1.5 py-0.5 rounded bg-zinc-800 border border-zinc-700">{c}</span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Top Offers */}
          {trending?.top_offers?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><TrendUp size={20} className="text-emerald-400" /> Top Offers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {trending.top_offers.slice(0, 6).map((offer, i) => (
                  <Card key={offer.offer_id || i} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                    onClick={() => navigate('/marketplace')}
                    data-testid={`trending-offer-${i}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <h3 className="font-medium text-sm text-zinc-200">{offer.title}</h3>
                        <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 text-xs shrink-0 ml-2">${offer.payout}</Badge>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{offer.description}</p>
                      <div className="flex items-center gap-2 mt-2 text-[10px] text-zinc-600">
                        <span>{offer.type}</span><span>&middot;</span><span>{offer.category}</span>
                        <span>&middot;</span><span>{offer.total_conversions?.toLocaleString() || 0} conv.</span>
                      </div>
                      <div className="mt-3 text-[11px] text-blue-400 font-medium">View in Marketplace &rarr;</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Popular Collections */}
          {trending?.popular_collections?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Stack size={20} className="text-blue-400" /> Popular Collections</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {trending.popular_collections.slice(0, 6).map((col, i) => (
                  <Card key={col.collection_id || i} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                    onClick={() => navigate('/discover')}
                    data-testid={`trending-collection-${i}`}
                  >
                    <CardContent className="p-4">
                      <h3 className="font-medium text-sm text-zinc-200">{col.name}</h3>
                      <p className="text-xs text-zinc-500 mt-1">{col.description}</p>
                      <div className="text-[10px] text-zinc-600 mt-2">{col.items?.length || 0} items &middot; {col.views || 0} views</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
